// src/api/admin.js
/**
 * АДМИНСКИЙ КЛИЕНТ ДЛЯ API (JWT, обновление access по refresh)
 */
import {
  API_BASE,
  buildQuery,
  normalizeMediaItem,
  api,
  apiForm,
} from './base';

export const AdminAPI = {
  /* ---- AUTH ---- */
  async login(username, password) {
    const data = await api('/auth/login', { method: 'POST', body: { username, password } });
    
    // Наш тестовый бэкенд возвращает { token, user }, а не { access_token, refresh_token }
    const result = { 
      token: data.token || data.access_token, 
      refresh: data.refresh_token, 
      user: data.user 
    };
    return result;
  },
  me: () => api('/auth/me'),

  /* ---- MEDIA LIBRARY ---- */
  listMediaLibrary(params = {}) {
    return api(`/admin/media${buildQuery(params)}`).then((data) => ({
      ...data,
      media: (data.media || []).map(normalizeMediaItem),
    }));
  },
  uploadMedia(file, { alt_text_ru, alt_text_en, caption_ru, caption_en } = {}) {
    const fd = new FormData();
    fd.append('file', file);
    if (alt_text_ru) fd.append('alt_text_ru', alt_text_ru);
    if (alt_text_en) fd.append('alt_text_en', alt_text_en);
    if (caption_ru)  fd.append('caption_ru',  caption_ru);
    if (caption_en)  fd.append('caption_en',  caption_en);
    return apiForm('/admin/media', fd, { method: 'POST' })
      .then((data) => ({ ...data, file: normalizeMediaItem(data.file) }));
  },
  mediaInfo:   (id) => api(`/admin/media/${id}`).then((d) => ({ ...d, file: normalizeMediaItem(d.file) })),
  getMediaFile: (id) => api(`/admin/media/${id}`).then((d) => normalizeMediaItem(d.file || d)),
  updateMedia: (id, payload) => api(`/admin/media/${id}`, { method: 'PUT', body: payload }),
  deleteMedia: (id) => api(`/admin/media/${id}`, { method: 'DELETE' }),
  uploadUrl: () => `${API_BASE}/admin/media`,

  /* ---- PROPERTIES ---- */
  listProperties:  (params = {}) => api(`/admin/properties${buildQuery(params)}`),
  getProperty:     (id, params = {}) => api(`/admin/properties/${id}${buildQuery(params)}`),
  createProperty:  (payload)     => api('/admin/properties', { method: 'POST', body: payload }),
  updateProperty:  (id, payload) => api(`/admin/properties/${id}`, { method: 'PUT', body: payload }),
  deleteProperty:  (id)          => api(`/admin/properties/${id}`, { method: 'DELETE' }),

  /* ---- SPECIALIZED PROPERTY TYPES (different tables) ---- */
  // Зарубежная недвижимость
  listForeignProperties:  (params = {}) => api(`/admin/foreign-properties${buildQuery(params)}`),
  getForeignProperty:     (id, params = {}) => api(`/admin/foreign-properties/${id}${buildQuery(params)}`),
  createForeignProperty:  (payload)     => api('/admin/foreign-properties', { method: 'POST', body: payload }),
  updateForeignProperty:  (id, payload) => api(`/admin/foreign-properties/${id}`, { method: 'PUT', body: payload }),
  deleteForeignProperty:  (id)          => api(`/admin/foreign-properties/${id}`, { method: 'DELETE' }),

  // Загородная недвижимость
  listSuburbanProperties:  (params = {}) => api(`/admin/suburban-properties${buildQuery(params)}`),
  getSuburbanProperty:     (id, params = {}) => api(`/admin/suburban-properties/${id}${buildQuery(params)}`),
  createSuburbanProperty:  (payload)     => api('/admin/suburban-properties', { method: 'POST', body: payload }),
  updateSuburbanProperty:  (id, payload) => api(`/admin/suburban-properties/${id}`, { method: 'PUT', body: payload }),
  deleteSuburbanProperty:  (id)          => api(`/admin/suburban-properties/${id}`, { method: 'DELETE' }),

  // Коммерческая аренда
  listCommercialRentProperties:  (params = {}) => api(`/admin/commercial-rent-properties${buildQuery(params)}`),
  getCommercialRentProperty:     (id, params = {}) => api(`/admin/commercial-rent-properties/${id}${buildQuery(params)}`),
  createCommercialRentProperty:  (payload)     => api('/admin/commercial-rent-properties', { method: 'POST', body: payload }),
  updateCommercialRentProperty:  (id, payload) => api(`/admin/commercial-rent-properties/${id}`, { method: 'PUT', body: payload }),
  deleteCommercialRentProperty:  (id)          => api(`/admin/commercial-rent-properties/${id}`, { method: 'DELETE' }),

  // Паркинги
  listParkingProperties:  (params = {}) => api(`/admin/parking-properties${buildQuery(params)}`),
  getParkingProperty:     (id, params = {}) => api(`/admin/parking-properties/${id}${buildQuery(params)}`),
  createParkingProperty:  (payload)     => api('/admin/parking-properties', { method: 'POST', body: payload }),
  updateParkingProperty:  (id, payload) => api(`/admin/parking-properties/${id}`, { method: 'PUT', body: payload }),
  deleteParkingProperty:  (id)          => api(`/admin/parking-properties/${id}`, { method: 'DELETE' }),

  // Флаги для специализированных типов
  setFeaturedForeignProperty: (propertyId, enabled = true) =>
    api(`/admin/foreign-properties/${propertyId}/featured`, { method: 'POST', body: { enabled: !!enabled } }),
  setInvestmentForeignProperty: (propertyId, enabled = true) =>
    api(`/admin/foreign-properties/${propertyId}/investment`, { method: 'POST', body: { enabled: !!enabled } }),

  setFeaturedSuburbanProperty: (propertyId, enabled = true) =>
    api(`/admin/suburban-properties/${propertyId}/featured`, { method: 'POST', body: { enabled: !!enabled } }),
  setInvestmentSuburbanProperty: (propertyId, enabled = true) =>
    api(`/admin/suburban-properties/${propertyId}/investment`, { method: 'POST', body: { enabled: !!enabled } }),

  setFeaturedCommercialRentProperty: (propertyId, enabled = true) =>
    api(`/admin/commercial-rent-properties/${propertyId}/featured`, { method: 'POST', body: { enabled: !!enabled } }),
  setInvestmentCommercialRentProperty: (propertyId, enabled = true) =>
    api(`/admin/commercial-rent-properties/${propertyId}/investment`, { method: 'POST', body: { enabled: !!enabled } }),

  setFeaturedParkingProperty: (propertyId, enabled = true) =>
    api(`/admin/parking-properties/${propertyId}/featured`, { method: 'POST', body: { enabled: !!enabled } }),
  setInvestmentParkingProperty: (propertyId, enabled = true) =>
    api(`/admin/parking-properties/${propertyId}/investment`, { method: 'POST', body: { enabled: !!enabled } }),

  /* ---- PROPERTY MEDIA (link MediaFile to Property) ---- */
  listPropertyMedia: (propertyId) =>
    api(`/admin/properties/${propertyId}/media`),
  attachPropertyMedia: (propertyId, payload /* { media_id, is_main?, sort_order?, is_active?, is_hero_slider?, viz_type?, viz_title_ru?, viz_title_en?, viz_type_id?, viz_type_slug? } */) =>
    api(`/admin/properties/${propertyId}/media`, { method: 'POST', body: payload }),
  updatePropertyMedia: (propertyId, linkId, patch /* { is_main?, is_active?, is_hero_slider?, sort_order?, viz_type?, viz_title_ru?, viz_title_en?, viz_type_id?, viz_type_slug? } */) =>
    api(`/admin/properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: patch }),
  deletePropertyMedia: (propertyId, linkId) =>
    api(`/admin/properties/${propertyId}/media/${linkId}`, { method: 'DELETE' }),
  setMainPropertyMedia: (propertyId, linkId) =>
    api(`/admin/properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: { is_main: true } }),
  reorderPropertyMedia: (propertyId, order /* [linkId1, linkId2,...] */) =>
    api(`/admin/properties/${propertyId}/media/reorder`, { method: 'POST', body: { order } }),

  /* ---- SPECIALIZED PROPERTY MEDIA ---- */
  // Foreign Properties Media
  listForeignPropertyMedia: (propertyId) =>
    api(`/admin/foreign-properties/${propertyId}/media`),
  attachForeignPropertyMedia: (propertyId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/media`, { method: 'POST', body: payload }),
  updateForeignPropertyMedia: (propertyId, linkId, patch) =>
    api(`/admin/foreign-properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: patch }),
  deleteForeignPropertyMedia: (propertyId, linkId) =>
    api(`/admin/foreign-properties/${propertyId}/media/${linkId}`, { method: 'DELETE' }),
  setMainForeignPropertyMedia: (propertyId, linkId) =>
    api(`/admin/foreign-properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: { is_main: true } }),
  reorderForeignPropertyMedia: (propertyId, order) =>
    api(`/admin/foreign-properties/${propertyId}/media/reorder`, { method: 'POST', body: { order } }),

  // Suburban Properties Media
  listSuburbanPropertyMedia: (propertyId) =>
    api(`/admin/suburban-properties/${propertyId}/media`),
  attachSuburbanPropertyMedia: (propertyId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/media`, { method: 'POST', body: payload }),
  updateSuburbanPropertyMedia: (propertyId, linkId, patch) =>
    api(`/admin/suburban-properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: patch }),
  deleteSuburbanPropertyMedia: (propertyId, linkId) =>
    api(`/admin/suburban-properties/${propertyId}/media/${linkId}`, { method: 'DELETE' }),
  setMainSuburbanPropertyMedia: (propertyId, linkId) =>
    api(`/admin/suburban-properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: { is_main: true } }),
  reorderSuburbanPropertyMedia: (propertyId, order) =>
    api(`/admin/suburban-properties/${propertyId}/media/reorder`, { method: 'POST', body: { order } }),

  // Commercial Rent Properties Media
  listCommercialRentPropertyMedia: (propertyId) =>
    api(`/admin/commercial-rent-properties/${propertyId}/media`),
  attachCommercialRentPropertyMedia: (propertyId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/media`, { method: 'POST', body: payload }),
  updateCommercialRentPropertyMedia: (propertyId, linkId, patch) =>
    api(`/admin/commercial-rent-properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: patch }),
  deleteCommercialRentPropertyMedia: (propertyId, linkId) =>
    api(`/admin/commercial-rent-properties/${propertyId}/media/${linkId}`, { method: 'DELETE' }),
  setMainCommercialRentPropertyMedia: (propertyId, linkId) =>
    api(`/admin/commercial-rent-properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: { is_main: true } }),
  reorderCommercialRentPropertyMedia: (propertyId, order) =>
    api(`/admin/commercial-rent-properties/${propertyId}/media/reorder`, { method: 'POST', body: { order } }),

  // Parking Properties Media
  listParkingPropertyMedia: (propertyId) =>
    api(`/admin/parking-properties/${propertyId}/media`),
  attachParkingPropertyMedia: (propertyId, payload) =>
    api(`/admin/parking-properties/${propertyId}/media`, { method: 'POST', body: payload }),
  updateParkingPropertyMedia: (propertyId, linkId, patch) =>
    api(`/admin/parking-properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: patch }),
  deleteParkingPropertyMedia: (propertyId, linkId) =>
    api(`/admin/parking-properties/${propertyId}/media/${linkId}`, { method: 'DELETE' }),
  setMainParkingPropertyMedia: (propertyId, linkId) =>
    api(`/admin/parking-properties/${propertyId}/media/${linkId}`, { method: 'PUT', body: { is_main: true } }),
  reorderParkingPropertyMedia: (propertyId, order) =>
    api(`/admin/parking-properties/${propertyId}/media/reorder`, { method: 'POST', body: { order } }),
  /* ---- PAYMENT PLANS ---- */
  listPaymentPlans: (propertyId, params = {}) =>
    api(`/admin/properties/${propertyId}/payment-plans${buildQuery(params)}`),

  createPaymentPlan: (propertyId, payload) =>
    api(`/admin/properties/${propertyId}/payment-plans`, { method: 'POST', body: payload }),

  updatePaymentPlan: (planId, payload) =>
    api(`/admin/payment-plans/${planId}`, { method: 'PUT', body: payload }),

  deletePaymentPlan: (planId) =>
    api(`/admin/payment-plans/${planId}`, { method: 'DELETE' }),

  createPaymentStage: (planId, payload) =>
    api(`/admin/payment-plans/${planId}/stages`, { method: 'POST', body: payload }),

  updatePaymentStage: (stageId, payload) =>
    api(`/admin/payment-plan-stages/${stageId}`, { method: 'PUT', body: payload }),

  deletePaymentStage: (stageId) =>
    api(`/admin/payment-plan-stages/${stageId}`, { method: 'DELETE' }),

  /* ---- SPECIALIZED PROPERTY PAYMENT PLANS ---- */
  // Foreign Properties Payment Plans
  listForeignPaymentPlans: (propertyId, params = {}) =>
    api(`/admin/foreign-properties/${propertyId}/payment-plans${buildQuery(params)}`),
  createForeignPaymentPlan: (propertyId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/payment-plans`, { method: 'POST', body: payload }),

  // Suburban Properties Payment Plans
  listSuburbanPaymentPlans: (propertyId, params = {}) =>
    api(`/admin/suburban-properties/${propertyId}/payment-plans${buildQuery(params)}`),
  createSuburbanPaymentPlan: (propertyId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/payment-plans`, { method: 'POST', body: payload }),

  // Commercial Rent Properties Payment Plans
  listCommercialRentPaymentPlans: (propertyId, params = {}) =>
    api(`/admin/commercial-rent-properties/${propertyId}/payment-plans${buildQuery(params)}`),
  createCommercialRentPaymentPlan: (propertyId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/payment-plans`, { method: 'POST', body: payload }),

  // Parking Properties Payment Plans
  listParkingPaymentPlans: (propertyId, params = {}) =>
    api(`/admin/parking-properties/${propertyId}/payment-plans${buildQuery(params)}`),
  createParkingPaymentPlan: (propertyId, payload) =>
    api(`/admin/parking-properties/${propertyId}/payment-plans`, { method: 'POST', body: payload }),

  /* ---- PROPERTY AMENITIES (link) ---- */
  listPropertyAmenities: (propertyId, params = {}) =>
    api(`/admin/properties/${propertyId}/amenities${buildQuery(params)}`),

  attachPropertyAmenity: (propertyId, payload /* { amenity_id, sort_order?, is_active? } */) =>
    api(`/admin/properties/${propertyId}/amenities`, { method: 'POST', body: payload }),

  updatePropertyAmenity: (propertyId, linkId, payload /* { sort_order?, is_active? } */) =>
    api(`/admin/properties/${propertyId}/amenities/${linkId}`, { method: 'PUT', body: payload }),

  deletePropertyAmenity: (propertyId, linkId) =>
    api(`/admin/properties/${propertyId}/amenities/${linkId}`, { method: 'DELETE' }),

  reorderPropertyAmenities: (propertyId, order /* [linkId1, linkId2,...] */) =>
    api(`/admin/properties/${propertyId}/amenities/reorder`, { method: 'POST', body: { order } }),

  /* ---- SPECIALIZED PROPERTY AMENITIES ---- */
  // Foreign Properties Amenities
  listForeignPropertyAmenities: (propertyId, params = {}) =>
    api(`/admin/foreign-properties/${propertyId}/amenities${buildQuery(params)}`),
  attachForeignPropertyAmenity: (propertyId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/amenities`, { method: 'POST', body: payload }),
  updateForeignPropertyAmenity: (propertyId, linkId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/amenities/${linkId}`, { method: 'PUT', body: payload }),
  deleteForeignPropertyAmenity: (propertyId, linkId) =>
    api(`/admin/foreign-properties/${propertyId}/amenities/${linkId}`, { method: 'DELETE' }),
  reorderForeignPropertyAmenities: (propertyId, order) =>
    api(`/admin/foreign-properties/${propertyId}/amenities/reorder`, { method: 'POST', body: { order } }),

  // Suburban Properties Amenities
  listSuburbanPropertyAmenities: (propertyId, params = {}) =>
    api(`/admin/suburban-properties/${propertyId}/amenities${buildQuery(params)}`),
  attachSuburbanPropertyAmenity: (propertyId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/amenities`, { method: 'POST', body: payload }),
  updateSuburbanPropertyAmenity: (propertyId, linkId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/amenities/${linkId}`, { method: 'PUT', body: payload }),
  deleteSuburbanPropertyAmenity: (propertyId, linkId) =>
    api(`/admin/suburban-properties/${propertyId}/amenities/${linkId}`, { method: 'DELETE' }),
  reorderSuburbanPropertyAmenities: (propertyId, order) =>
    api(`/admin/suburban-properties/${propertyId}/amenities/reorder`, { method: 'POST', body: { order } }),

  // Commercial Rent Properties Amenities
  listCommercialRentPropertyAmenities: (propertyId, params = {}) =>
    api(`/admin/commercial-rent-properties/${propertyId}/amenities${buildQuery(params)}`),
  attachCommercialRentPropertyAmenity: (propertyId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/amenities`, { method: 'POST', body: payload }),
  updateCommercialRentPropertyAmenity: (propertyId, linkId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/amenities/${linkId}`, { method: 'PUT', body: payload }),
  deleteCommercialRentPropertyAmenity: (propertyId, linkId) =>
    api(`/admin/commercial-rent-properties/${propertyId}/amenities/${linkId}`, { method: 'DELETE' }),
  reorderCommercialRentPropertyAmenities: (propertyId, order) =>
    api(`/admin/commercial-rent-properties/${propertyId}/amenities/reorder`, { method: 'POST', body: { order } }),

  // Parking Properties Amenities
  listParkingPropertyAmenities: (propertyId, params = {}) =>
    api(`/admin/parking-properties/${propertyId}/amenities${buildQuery(params)}`),
  attachParkingPropertyAmenity: (propertyId, payload) =>
    api(`/admin/parking-properties/${propertyId}/amenities`, { method: 'POST', body: payload }),
  updateParkingPropertyAmenity: (propertyId, linkId, payload) =>
    api(`/admin/parking-properties/${propertyId}/amenities/${linkId}`, { method: 'PUT', body: payload }),
  deleteParkingPropertyAmenity: (propertyId, linkId) =>
    api(`/admin/parking-properties/${propertyId}/amenities/${linkId}`, { method: 'DELETE' }),
  reorderParkingPropertyAmenities: (propertyId, order) =>
    api(`/admin/parking-properties/${propertyId}/amenities/reorder`, { method: 'POST', body: { order } }),

  /* ---- PROPERTY ↔ MANAGERS ---- */
  listPropertyManagers: (propertyId, params = {}) =>
    api(`/admin/properties/${propertyId}/managers${buildQuery(params)}`),

  attachPropertyManager: (propertyId, payload /* { manager_id, is_primary?, sort_order? } */) =>
    api(`/admin/properties/${propertyId}/managers`, { method: 'POST', body: payload }),

  updatePropertyManager: (propertyId, linkId, payload /* { is_primary?, sort_order? } */) =>
    api(`/admin/properties/${propertyId}/managers/${linkId}`, { method: 'PUT', body: payload }),

  deletePropertyManager: (propertyId, linkId) =>
    api(`/admin/properties/${propertyId}/managers/${linkId}`, { method: 'DELETE' }),

  /* ---- SPECIALIZED PROPERTY MANAGERS ---- */
  // Foreign Properties Managers
  listForeignPropertyManagers: (propertyId, params = {}) =>
    api(`/admin/foreign-properties/${propertyId}/managers${buildQuery(params)}`),
  attachForeignPropertyManager: (propertyId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/managers`, { method: 'POST', body: payload }),
  updateForeignPropertyManager: (propertyId, linkId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/managers/${linkId}`, { method: 'PUT', body: payload }),
  deleteForeignPropertyManager: (propertyId, linkId) =>
    api(`/admin/foreign-properties/${propertyId}/managers/${linkId}`, { method: 'DELETE' }),

  // Suburban Properties Managers
  listSuburbanPropertyManagers: (propertyId, params = {}) =>
    api(`/admin/suburban-properties/${propertyId}/managers${buildQuery(params)}`),
  attachSuburbanPropertyManager: (propertyId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/managers`, { method: 'POST', body: payload }),
  updateSuburbanPropertyManager: (propertyId, linkId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/managers/${linkId}`, { method: 'PUT', body: payload }),
  deleteSuburbanPropertyManager: (propertyId, linkId) =>
    api(`/admin/suburban-properties/${propertyId}/managers/${linkId}`, { method: 'DELETE' }),

  // Commercial Rent Properties Managers
  listCommercialRentPropertyManagers: (propertyId, params = {}) =>
    api(`/admin/commercial-rent-properties/${propertyId}/managers${buildQuery(params)}`),
  attachCommercialRentPropertyManager: (propertyId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/managers`, { method: 'POST', body: payload }),
  updateCommercialRentPropertyManager: (propertyId, linkId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/managers/${linkId}`, { method: 'PUT', body: payload }),
  deleteCommercialRentPropertyManager: (propertyId, linkId) =>
    api(`/admin/commercial-rent-properties/${propertyId}/managers/${linkId}`, { method: 'DELETE' }),

  // Parking Properties Managers
  listParkingPropertyManagers: (propertyId, params = {}) =>
    api(`/admin/parking-properties/${propertyId}/managers${buildQuery(params)}`),
  attachParkingPropertyManager: (propertyId, payload) =>
    api(`/admin/parking-properties/${propertyId}/managers`, { method: 'POST', body: payload }),
  updateParkingPropertyManager: (propertyId, linkId, payload) =>
    api(`/admin/parking-properties/${propertyId}/managers/${linkId}`, { method: 'PUT', body: payload }),
  deleteParkingPropertyManager: (propertyId, linkId) =>
    api(`/admin/parking-properties/${propertyId}/managers/${linkId}`, { method: 'DELETE' }),
  
  /* ---- UNITS (admin) ---- */
  listUnits: (propertyId, params = {}) =>
    api(`/admin/properties/${propertyId}/units${buildQuery(params)}`),

  getUnit: (unitId, params = {}) =>
    api(`/admin/units/${unitId}${buildQuery(params)}`),

  createUnit: (propertyId, payload) =>
    api(`/admin/properties/${propertyId}/units`, { method: 'POST', body: payload }),

  updateUnit: (unitId, payload) =>
    api(`/admin/units/${unitId}`, { method: 'PUT', body: payload }),

  deleteUnit: (unitId) =>
    api(`/admin/units/${unitId}`, { method: 'DELETE' }),



  /* ---- UNIT MEDIA (link MediaFile to Unit) ---- */
  listUnitMedia: (unitId) =>
    api(`/admin/units/${unitId}/media`),

  attachUnitMedia: (unitId, payload /* { media_id, is_main?, sort_order?, is_active? } */) =>
    api(`/admin/units/${unitId}/media`, { method: 'POST', body: payload }),

  updateUnitMedia: (unitId, linkId, payload /* { is_main?, sort_order?, is_active? } */) =>
    api(`/admin/units/${unitId}/media/${linkId}`, { method: 'PUT', body: payload }),

  deleteUnitMedia: (unitId, linkId) =>
    api(`/admin/units/${unitId}/media/${linkId}`, { method: 'DELETE' }),

  /* ---- SPECIALIZED PROPERTY UNITS ---- */
  // Foreign Properties Units
  listForeignUnits: (propertyId, params = {}) =>
    api(`/admin/foreign-properties/${propertyId}/units${buildQuery(params)}`),
  createForeignUnit: (propertyId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/units`, { method: 'POST', body: payload }),
  updateForeignUnit: (propertyId, unitId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/units/${unitId}`, { method: 'PUT', body: payload }),
  deleteForeignUnit: (propertyId, unitId) =>
    api(`/admin/foreign-properties/${propertyId}/units/${unitId}`, { method: 'DELETE' }),

  // Suburban Properties Units
  listSuburbanUnits: (propertyId, params = {}) =>
    api(`/admin/suburban-properties/${propertyId}/units${buildQuery(params)}`),
  createSuburbanUnit: (propertyId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/units`, { method: 'POST', body: payload }),
  updateSuburbanUnit: (propertyId, unitId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/units/${unitId}`, { method: 'PUT', body: payload }),
  deleteSuburbanUnit: (propertyId, unitId) =>
    api(`/admin/suburban-properties/${propertyId}/units/${unitId}`, { method: 'DELETE' }),

  // Commercial Rent Properties Units
  listCommercialRentUnits: (propertyId, params = {}) =>
    api(`/admin/commercial-rent-properties/${propertyId}/units${buildQuery(params)}`),
  createCommercialRentUnit: (propertyId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/units`, { method: 'POST', body: payload }),
  updateCommercialRentUnit: (propertyId, unitId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/units/${unitId}`, { method: 'PUT', body: payload }),
  deleteCommercialRentUnit: (propertyId, unitId) =>
    api(`/admin/commercial-rent-properties/${propertyId}/units/${unitId}`, { method: 'DELETE' }),

  // Parking Properties Units
  listParkingUnits: (propertyId, params = {}) =>
    api(`/admin/parking-properties/${propertyId}/units${buildQuery(params)}`),
  createParkingUnit: (propertyId, payload) =>
    api(`/admin/parking-properties/${propertyId}/units`, { method: 'POST', body: payload }),
  updateParkingUnit: (propertyId, unitId, payload) =>
    api(`/admin/parking-properties/${propertyId}/units/${unitId}`, { method: 'PUT', body: payload }),
  deleteParkingUnit: (propertyId, unitId) =>
    api(`/admin/parking-properties/${propertyId}/units/${unitId}`, { method: 'DELETE' }),
  
  /* ---- MANAGERS (admin) ---- */
  // GET /admin/managers
  listManagers: (params = {}) =>
    api(`/admin/managers${buildQuery(params)}`),
  // GET /admin/managers/:id
  getManager: (id, params = {}) =>
    api(`/admin/managers/${id}${buildQuery(params)}`),
  // POST /admin/managers
  createManager: (payload) =>
    api('/admin/managers', { method: 'POST', body: payload }),
  // PUT /admin/managers/:id
  updateManager: (id, payload) =>
    api(`/admin/managers/${id}`, { method: 'PUT', body: payload }),
  // DELETE /admin/managers/:id  (+?hard=1 по желанию)
  deleteManager: (id, { hard = false } = {}) =>
    api(`/admin/managers/${id}${buildQuery(hard ? { hard: 1 } : {})}`, { method: 'DELETE' }),
  /* ---- HERO (админка, теперь множественный) ---- */
  // список текущих героев (в порядке показа)
  homepageList: () => api('/admin/properties/homepage-list'),
  // список инвестиционных объектов для главной
  investmentList: () => api('/admin/properties/investment-list'),
  // включить/выключить флаг героя для конкретного объекта
  homepageSet: (propertyId, enabled = true) =>
    api(`/admin/properties/${propertyId}/homepage`, { method: 'POST', body: { enabled: !!enabled } }),
  // задать порядок героев: массив ID в нужной последовательности
  homepageReorder: (order /* [propertyId1, propertyId2, ...] */) =>
    api('/admin/properties/homepage/reorder', { method: 'POST', body: { order } }),

  // РЕКОМЕНДУЕМЫЕ (featured flag)
  setFeaturedProperty: (propertyId, enabled = true) =>
    api(`/admin/properties/${propertyId}/featured`, { method: 'POST', body: { enabled: !!enabled } }),

  // ИНВЕСТИЦИОННЫЕ (investment flag)
  setInvestmentProperty: (propertyId, enabled = true) =>
    api(`/admin/properties/${propertyId}/investment`, { method: 'POST', body: { enabled: !!enabled } }),

  // (legacy совместимость — оставим алиасы)
  homepageGet: () => api('/admin/properties/homepage'), // вернет {property: null} или первый из списка, если сервер так реализует
  setHomepage: (propertyId, enabled) =>
    api(`/admin/properties/${propertyId}/homepage`, { method: 'POST', body: { enabled: !!enabled } }),

  /* ---- REGIONS (admin) ---- */
  listRegions:   (params = {}) => api(`/admin/regions${buildQuery(params)}`),
  createRegion:  (payload)     => api('/admin/regions', { method: 'POST', body: payload }),
  updateRegion:  (id, payload) => api(`/admin/regions/${id}`, { method: 'PUT', body: payload }),
  deleteRegion:  (id)          => api(`/admin/regions/${id}`, { method: 'DELETE' }),

  /* ---- DISTRICTS (admin) ---- */
  listDistricts:  (params = {}) => api(`/admin/districts${buildQuery(params)}`),
  createDistrict: (payload)     => api('/admin/districts', { method: 'POST', body: payload }),
  updateDistrict: (id, payload) => api(`/admin/districts/${id}`, { method: 'PUT', body: payload }),
  deleteDistrict: (id)          => api(`/admin/districts/${id}`, { method: 'DELETE' }),

  // DISTRICT MEDIA
  listDistrictMedia:   (districtId) => api(`/admin/districts/${districtId}/media`),
  attachDistrictMedia: (districtId, payload /* { media_id, is_main?, sort_order?, is_active? } */) =>
    api(`/admin/districts/${districtId}/media`, { method: 'POST', body: payload }),
  updateDistrictMedia: (districtId, linkId, patch /* { is_main?, is_active?, sort_order? } */) =>
    api(`/admin/districts/${districtId}/media/${linkId}`, { method: 'PUT', body: patch }),
  deleteDistrictMedia: (districtId, linkId) =>
    api(`/admin/districts/${districtId}/media/${linkId}`, { method: 'DELETE' }),
  reorderDistrictMedia: (districtId, order /* [linkId1, linkId2,...] */) =>
    api(`/admin/districts/${districtId}/media/reorder`, { method: 'POST', body: { order } }),

  /* ---- LEADS ---- */
  leads:      (params = {}) => api(`/admin/leads${buildQuery(params)}`),
  getLead:    (id, params = {}) => api(`/admin/leads/${id}${buildQuery(params)}`),
  updateLead: (id, payload) => api(`/admin/leads/${id}`, { method: 'PUT', body: payload }),

  /* ---- CURRENCIES (админка) ---- */
  listCurrencyRates: (params = {}) => api(`/admin/currencies/rates${buildQuery(params)}`),
  currencyRate: (code, params = {}) =>
    api(`/admin/currencies/rates/${encodeURIComponent(code)}${buildQuery(params)}`),
  updateRates: (currencies) => 
    api('/admin/currencies/rates', { method: 'PUT', body: { currencies } }),
  autoUpdateCurrencyRates: (params = {}) =>
    api('/admin/currencies/auto-update', { method: 'POST', body: params }),
  getCurrencyStatus: () => api('/admin/currencies/status'),

  /* ---- POSTS (admin: blog/news/media) ---- */
  listPosts: (params = {}) => api(`/admin/posts${buildQuery(params)}`),
  getPost:   (id)          => api(`/admin/posts/${id}`),
  createPost: (payload)    => api('/admin/posts', { method: 'POST', body: payload }),
  updatePost: (id, payload)=> api(`/admin/posts/${id}`, { method: 'PUT', body: payload }),
  deletePost: (id)         => api(`/admin/posts/${id}`, { method: 'DELETE' }),
  publishPost: (id, publish = true) =>
    api(`/admin/posts/${id}/publish`, { method: 'POST', body: { publish: !!publish } }),

  /* ---- CATALOGS (admin: AuthorCatalog) ---- */
  listCatalogs: (params = {}) => api(`/admin/catalogs${buildQuery(params)}`),
  getCatalog:   (id)          => api(`/admin/catalogs/${id}`),
  createCatalog: (payload)    => api('/admin/catalogs', { method: 'POST', body: payload }),
  updateCatalog: (id, payload)=> api(`/admin/catalogs/${id}`, { method: 'PUT', body: payload }),
  deleteCatalog: (id)         => api(`/admin/catalogs/${id}`, { method: 'DELETE' }),

  /* ---- COLLECTIONS (admin: PropertyCollection) ---- */
  listCollections:   (params = {}) => api(`/admin/collections${buildQuery(params)}`),
  getCollection:     (id, params = {}) => api(`/admin/collections/${id}${buildQuery(params)}`),
  createCollection:  (payload)     => api('/admin/collections', { method: 'POST', body: payload }),
  updateCollection:  (id, payload) => api(`/admin/collections/${id}`, { method: 'PUT', body: payload }),
  deleteCollection:  (id)          => api(`/admin/collections/${id}`, { method: 'DELETE' }),

  // items of a collection
  listCollectionItems: (collectionId) =>
    api(`/admin/collections/${collectionId}/items`),
  addCollectionItem: (collectionId, payload /* { property_id, sort_order? } */) =>
    api(`/admin/collections/${collectionId}/items`, { method: 'POST', body: payload }),
  updateCollectionItem: (collectionId, itemId, payload /* { sort_order?, is_active? } */) =>
    api(`/admin/collections/${collectionId}/items/${itemId}`, { method: 'PUT', body: payload }),
  deleteCollectionItem: (collectionId, itemId) =>
    api(`/admin/collections/${collectionId}/items/${itemId}`, { method: 'DELETE' }),

    /* ---- AMENITIES (admin) ---- */
  // GET /admin/amenities
  listAmenities: (params = {}) =>
    api(`/admin/amenities${buildQuery(params)}`),

  // GET /admin/amenities/:id
  getAmenity: (id, params = {}) =>
    api(`/admin/amenities/${id}${buildQuery(params)}`),

  // POST /admin/amenities
  createAmenity: (payload) =>
    api('/admin/amenities', { method: 'POST', body: payload }),

  // PUT /admin/amenities/:id
  updateAmenity: (id, payload) =>
    api(`/admin/amenities/${id}`, { method: 'PUT', body: payload }),

  // DELETE /admin/amenities/:id
  deleteAmenity: (id) =>
    api(`/admin/amenities/${id}`, { method: 'DELETE' }),

  // (опционально, отдельная установка/сброс обложки)
  setAmenityImage: (id, media_id /* number|null */) =>
    api(`/admin/amenities/${id}/image`, { method: 'PUT', body: { media_id } }),

  /* ---- AWARDS (admin) ---- */
  listAwards:    (params = {}) => api(`/admin/awards${buildQuery(params)}`),
  getAward:      (id)          => api(`/admin/awards/${id}`),
  createAward:   (payload)     => api('/admin/awards', { method: 'POST', body: payload }),
  updateAward:   (id, payload) => api(`/admin/awards/${id}`, { method: 'PUT', body: payload }),
  deleteAward:   (id)          => api(`/admin/awards/${id}`, { method: 'DELETE' }),
  reorderAwards: (items /* [{id, sort_order}] */) =>
    api('/admin/awards/reorder', { method: 'POST', body: { items } }),

  /* ---- CORPORATION CONTENT (admin) ---- */
  getCorporationContent: () => api('/admin/corporation'),
  updateCorporationContent: (payload) => 
    api('/admin/corporation', { method: 'PUT', body: payload }),

  /* ---- SERVICES (admin) ---- */
  listServices: (params = {}) => api(`/admin/services${buildQuery(params)}`),
  getService: (id, params = {}) => api(`/admin/services/${id}${buildQuery(params)}`),
  createService: (payload) => api('/admin/services', { method: 'POST', body: payload }),
  updateService: (id, payload) => api(`/admin/services/${id}`, { method: 'PUT', body: payload }),
  deleteService: (id) => api(`/admin/services/${id}`, { method: 'DELETE' }),
  reorderServices: (items /* [{id, sort_order}] */) =>
    api('/admin/services/reorder', { method: 'POST', body: { items } }),

  /* ---- SPECIALIZED UNIT MEDIA ---- */
  // Foreign Property Unit Media
  listForeignUnitMedia: (propertyId, unitId) =>
    api(`/admin/foreign-properties/${propertyId}/units/${unitId}/media`),
  attachForeignUnitMedia: (propertyId, unitId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/units/${unitId}/media`, { method: 'POST', body: payload }),
  updateForeignUnitMedia: (propertyId, unitId, mediaId, payload) =>
    api(`/admin/foreign-properties/${propertyId}/units/${unitId}/media/${mediaId}`, { method: 'PUT', body: payload }),
  deleteForeignUnitMedia: (propertyId, unitId, mediaId) =>
    api(`/admin/foreign-properties/${propertyId}/units/${unitId}/media/${mediaId}`, { method: 'DELETE' }),

  // Suburban Property Unit Media
  listSuburbanUnitMedia: (propertyId, unitId) =>
    api(`/admin/suburban-properties/${propertyId}/units/${unitId}/media`),
  attachSuburbanUnitMedia: (propertyId, unitId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/units/${unitId}/media`, { method: 'POST', body: payload }),
  updateSuburbanUnitMedia: (propertyId, unitId, mediaId, payload) =>
    api(`/admin/suburban-properties/${propertyId}/units/${unitId}/media/${mediaId}`, { method: 'PUT', body: payload }),
  deleteSuburbanUnitMedia: (propertyId, unitId, mediaId) =>
    api(`/admin/suburban-properties/${propertyId}/units/${unitId}/media/${mediaId}`, { method: 'DELETE' }),

  // Commercial Rent Property Unit Media
  listCommercialRentUnitMedia: (propertyId, unitId) =>
    api(`/admin/commercial-rent-properties/${propertyId}/units/${unitId}/media`),
  attachCommercialRentUnitMedia: (propertyId, unitId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/units/${unitId}/media`, { method: 'POST', body: payload }),
  updateCommercialRentUnitMedia: (propertyId, unitId, mediaId, payload) =>
    api(`/admin/commercial-rent-properties/${propertyId}/units/${unitId}/media/${mediaId}`, { method: 'PUT', body: payload }),
  deleteCommercialRentUnitMedia: (propertyId, unitId, mediaId) =>
    api(`/admin/commercial-rent-properties/${propertyId}/units/${unitId}/media/${mediaId}`, { method: 'DELETE' }),

  // Parking Property Unit Media
  listParkingUnitMedia: (propertyId, unitId) =>
    api(`/admin/parking-properties/${propertyId}/units/${unitId}/media`),
  attachParkingUnitMedia: (propertyId, unitId, payload) =>
    api(`/admin/parking-properties/${propertyId}/units/${unitId}/media`, { method: 'POST', body: payload }),
  updateParkingUnitMedia: (propertyId, unitId, mediaId, payload) =>
    api(`/admin/parking-properties/${propertyId}/units/${unitId}/media/${mediaId}`, { method: 'PUT', body: payload }),
  deleteParkingUnitMedia: (propertyId, unitId, mediaId) =>
    api(`/admin/parking-properties/${propertyId}/units/${unitId}/media/${mediaId}`, { method: 'DELETE' }),
};

const Admin = {
  API_BASE,
  AdminAPI,
  buildQuery,
  api,
  apiForm,
  normalizeMediaItem,
};

export default Admin;
